import _ from 'lodash';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {getDatadogRum} from '../datadog/datadogRum';
import {logCustomActionToDatadog} from '../profiler';
import {getCache} from './getCache';
import {type CachedDataWithSize, type LongTermCache} from './types';
import {getCacheStorageKey} from './util';

export type UseCachedDataParams<T> = {
  // the compareFunc is used to determine if the data from the cache is the same as the data from the server
  compareFunc?: 'refEquality' | 'deepEquality' | ((a?: T, b?: T) => boolean);
  dataFromServer: T;
  key: any;
  namespace: string;
  waitingForDataFromServer: boolean;
};

/**
 * READ BEFORE USING THIS HOOK. SRSLY GUYS.
 * To use this hook properly, there are a couple of things to keep in mind:
 * 1. The caller must ensure that `namespace` is not shared between different queries.
 * 2. When `key == null`, this hook will no-op.
 * 3. When the query changes, the caller must ensure that `key` changes either at the same time or BEFORE `dataFromServer` changes.
 *    Otherwise, we will cache the new data under the old key, so the old key will have invalid data.
 */
export function useCachedData<T>({
  compareFunc,
  dataFromServer,
  key,
  namespace,
  waitingForDataFromServer,
}: UseCachedDataParams<T>): T {
  return useCachedDataWithOrigin({
    compareFunc,
    dataFromServer,
    key,
    namespace,
    waitingForDataFromServer,
  }).data;
}

const DEFAULT_COMPARE_FUNCS = {
  refEquality: (a: any, b: any) => a === b,
  deepEquality: (a: any, b: any) => _.isEqual(a, b),
};

export function useCachedDataWithOrigin<T>(
  {
    compareFunc = 'refEquality',
    dataFromServer,
    key,
    namespace,
    waitingForDataFromServer,
  }: UseCachedDataParams<T>,
  skip: boolean = false
): {data: T; origin: 'cache' | 'server'} {
  /**
   * The isMounted variable is used here as a flag to prevent calling setCachedState on an unmounted component. This is necessary because the cache.get call is asynchronous, and the component may unmount before it completes. By setting isMounted to false in the cleanup function, we ensure that setCachedState is only called if the component is still mounted, which helps avoid React warnings and potential memory leaks.
   */
  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const cache = getCache();

  const internalKey = useMemo(
    () => (skip ? null : getCacheStorageKey(namespace, key)),
    [namespace, key, skip]
  );

  // State holds both the current internalKey and the cache data for it
  const [cachedState, setCachedState] = useState<{
    internalKey: string | null;
    data: CachedDataWithSize<T> | null;
  }>({internalKey, data: null});

  const readCacheAndUpdateState = useCallback(
    async (internalKey: string | null, cache: LongTermCache, skip: boolean) => {
      if (!internalKey || !cache || skip) {
        return;
      }

      const fromCache = await cache.get<T>(internalKey);
      if (fromCache && isMounted.current) {
        setCachedState(prevState =>
          prevState.internalKey === internalKey
            ? {internalKey, data: fromCache}
            : prevState
        );
      }
    },
    []
  );

  /**
   * If the internalKey changes, immediately reset the cached data so that we don't return stale data while we're waiting for new data to be read from the cache or provided by the server
   */
  useEffect(() => {
    if (skip) {
      return;
    }
    setCachedState({internalKey, data: null});
  }, [internalKey, skip]);

  /**
   * Read Cache and Update State:
   * The only times we want to read from the cache and update the internal state of this hook are:
   * 1. When `skip` changes: as true => false means we want to start receiving updates
   * 2. When the `cache` changes value, e.g. if we loaded a cache or switched them out
   *   - I think it's possible that IF we switch caches after receiving a server response we wouldn't want to update the state from the switch-in cache given that it's likely more stale. However since right now we only have one cache backing this hook we don't need to worry about that.
   * 3. If the internal key changes we need to read the cache and update the state
   */
  useEffect(
    () => {
      readCacheAndUpdateState(internalKey, cache, skip);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [internalKey, cache, skip]
  );

  /**
   * When we get fresh data from the server, we need to:
   * 1) Update the state with the new data
   * 2) Syndicate that updated data to the cache
   *
   * We do not want to fire this when the internalKey changes, as we have to wait until `dataFromServer` updates to the new query result before we want to store it under the new `internalKey`. We handle nulling out the immediate value of this hook's state directly in the `useEffect` that handles the `internalKey` change.
   */
  useEffect(
    () => {
      if (
        skip || // skip the cache when skip is true
        waitingForDataFromServer || // skip when still waiting for data
        !internalKey || // skip the cache when we don't have a key to write under
        !cache // skip the cache when no cache is available
      ) {
        return;
      }

      /**
       * We do the equality check here as a safeguard against thrashing renders in the UI if the server is returning the same data over and over again (e.g. polling against unchanging data). For delta queries, the reference should be held stable higher up, so this deep equality check is not needed. However, the cost of the deep equality check is trivial compared to the render hit of certain components like line plots, so we'd rather be cautious and do the check for all hits on this hook.
       */
      if (
        typeof compareFunc === 'function'
          ? !compareFunc(cachedState.data?.data, dataFromServer)
          : !DEFAULT_COMPARE_FUNCS[compareFunc](
              cachedState.data?.data,
              dataFromServer
            )
      ) {
        // Update our state with the new data
        setCachedState(prevState =>
          prevState.internalKey === internalKey
            ? {internalKey, data: {data: dataFromServer, bytesReadFromCache: 0}}
            : prevState
        );

        // Store in cache
        cache.set(internalKey, dataFromServer);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dataFromServer]
  );

  // Measure durations (preserving original logic)
  useMeasureLoadDurations({
    namespace,
    dataFromCache: cachedState.data?.data,
    dataFromServer: waitingForDataFromServer ? null : dataFromServer,
    skip,
  });

  // Always return stable cached state
  const data = cachedState.data?.data ?? dataFromServer;
  const bytesReadFromCache = cachedState.data?.bytesReadFromCache ?? 0;
  const origin: 'cache' | 'server' =
    cachedState.data && bytesReadFromCache > 0 ? 'cache' : 'server';

  // Track cache usage (preserving original logic)
  useSetBytesReadFromCache(namespace, data, bytesReadFromCache, skip);

  return {
    data: cachedState.data?.data ?? dataFromServer,
    origin: skip ? 'server' : origin,
  };
}

type LoadTimestamps = {
  start: number;
  cache: number | null;
  server: number | null;
};

type UseMeasureLoadDurationsParams<T> = {
  namespace: string;
  dataFromCache: T | null;
  dataFromServer: T | null;
  skip: boolean;
};

// For now, to keep it simple, we only measure initial load durations.
// This means that even if `key` changes and new fetches from both cache and server are triggered,
// those load durations are not measured.
function useMeasureLoadDurations<T>({
  namespace,
  dataFromCache,
  dataFromServer,
  skip,
}: UseMeasureLoadDurationsParams<T>): void {
  const timestamps = useRef<LoadTimestamps>({
    start: Date.now(),
    cache: null,
    server: null,
  });
  const logged = useRef(false);

  const logLoadDurationsOnceIfReady = useCallback(() => {
    if (logged.current) {
      return;
    }

    const {start, cache, server} = timestamps.current;
    if (cache == null || server == null) {
      return;
    }

    const loadDurationCache = cache - start;
    const loadDurationServer = server - start;
    const serverDurationMinusCacheDuration =
      loadDurationServer - loadDurationCache;

    logCustomActionToDatadog(`cachehook.timeSaved`, {
      namespace,
      loadDurationCache,
      loadDurationServer,
      serverDurationMinusCacheDuration,
    });

    logged.current = true;
  }, [namespace]);

  useEffect(() => {
    if (skip) {
      return;
    }
    if (dataFromCache != null && timestamps.current.cache == null) {
      timestamps.current.cache = Date.now();
    }
    if (dataFromServer != null && timestamps.current.server == null) {
      timestamps.current.server = Date.now();
    }
    logLoadDurationsOnceIfReady();
  }, [dataFromCache, dataFromServer, logLoadDurationsOnceIfReady, skip]);
}

export function useSetBytesReadFromCache(
  namespace: string,
  result: any | null,
  bytesReadFromCache: number,
  skip: boolean
) {
  const haveSeenData = useRef(false);
  useEffect(() => {
    if (skip) {
      return;
    }
    if (result != null && !haveSeenData.current) {
      haveSeenData.current = true;
      getDatadogRum()?.setViewContextProperty(
        `${namespace}BytesReadFromCache`,
        bytesReadFromCache
      );
    }
  }, [result, bytesReadFromCache, namespace, skip]);
}
